import { useCallback, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure
} from "@chakra-ui/react";
import { CollecticonPlusSmall } from "@devseed-ui/collecticons-chakra";

import SelectFiles from "./SelectFiles";
import SelectModel from "./SelectModel";
import { useFileUpload } from "../../../../context";
import { Scene, UploadStatus } from "../../../../types/uploads";
import API from "../../../../api";
import { Model } from "../../../../types/scenes";

enum UploadProgress {
  SelectFiles = 1,
  SelectModel
}

const NEXT_PROGRESS = {
  [UploadProgress.SelectFiles]: UploadProgress.SelectModel
};

type UploadState = {
  files: Scene[];
  models: Model[];
  date?: string;
}

const initialUploadState = {
  files: [],
  models: []
};

type Props = {
  onSubmit: () => void;
  onUploadComplete: () => void;
}

function Upload({ onSubmit, onUploadComplete }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: confirmationIsOpen, onOpen: confirmationOnOpen, onClose: confirmationOnClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const {
    upload,
    status: sceneUploadStatus,
    cancelUpload: cancelUploadRequest
  } = useFileUpload();

  const [ uploadProgess, setUploadProgress ] = useState<UploadProgress>(UploadProgress.SelectFiles);
  const [ uploadState, setUploadState ] = useState<UploadState>(initialUploadState);
  const [ error, setError ] = useState<string>();

  const onNext = useCallback(async () => {
    const nextPage = NEXT_PROGRESS[uploadProgess as keyof typeof NEXT_PROGRESS];
    if (nextPage) {
      setUploadProgress(nextPage);
    } else {
      Promise.all(
        uploadState.files.map((currentFile) => {
          return uploadState.models.map((currentModel) => {
            return API.ingest(sceneUploadStatus[currentFile.key].sceneId, currentModel);
          });
        }).flat()
      )
        .then(resetState)
        .then(onSubmit)
        .then(onClose)
        .catch(() => setError("Unable to start ingestion."));
    }
  }, [onClose, onSubmit, sceneUploadStatus, uploadProgess, uploadState.files, uploadState.models]);

  const resetState = () => {
    setError("");
    setUploadState(initialUploadState);
    setUploadProgress(UploadProgress.SelectFiles);
  };

  const cancelUploads = () => {
    uploadState.files.map(({ key }) => cancelUploadRequest(key));
    resetState();
    confirmationOnClose();
    onClose();
  };

  const onCancel = useCallback(() => {
    const hasUploadsInProgress = uploadState.files.some(({ key }) => {
      const { multiSpectral, panchromatic } = sceneUploadStatus[key];
      return multiSpectral === UploadStatus.Uploading || panchromatic === UploadStatus.Uploading;
    });

    if (hasUploadsInProgress) {
      confirmationOnOpen();
    } else {
      resetState();
      onClose();
    }
  }, [confirmationOnOpen, onClose, sceneUploadStatus, uploadState.files]);

  const onFileSelect = useCallback((scene: Scene) => {
    const files = uploadState.files ? [...uploadState.files, scene] : [scene];
    setUploadState({
      ...uploadState,
      files
    });
    upload(scene, onUploadComplete);
  }, [onUploadComplete, upload, uploadState]);

  const onModelSelect = useCallback((models: Model[]) => {
    setUploadState({
      ...uploadState,
      models
    });
  }, [uploadState]);

  const modalContent = useMemo(() => {
    switch (uploadProgess) {
      case UploadProgress.SelectModel:
        return (
          <SelectModel
            files={uploadState.files}
            models={uploadState.models}
            onSelect={onModelSelect}
            onNext={onNext}
            onCancel={onCancel}
            ingestError={error}
          />);
      default:
        return (
          <SelectFiles
            files={uploadState.files}
            onSelect={onFileSelect}
            onNext={onNext}
            onCancel={onCancel}
          />);
    }
  }, [uploadProgess, uploadState.files, uploadState.models, onFileSelect, onModelSelect, onNext, onCancel, error]);

  return (
    <>
      <Box>
        <Button leftIcon={<CollecticonPlusSmall />} w="100%" onClick={onOpen}>Upload new scene</Button>
        <Modal isOpen={isOpen} onClose={onCancel} size="2xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Scene upload</ModalHeader>
            {(modalContent)}
          </ModalContent>
        </Modal>
      </Box>
      <AlertDialog
        isOpen={confirmationIsOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirmationOnClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              Cancel Uploads?
            </AlertDialogHeader>
            <AlertDialogBody>
              You have not yet selected models to run on your selected scenes.
              Cancelling without selecting models will cancel any ongoing scene uploads.
              Do you want to cancel scene uploads?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button variant="ghost" ref={cancelRef} onClick={confirmationOnClose}>
                No
              </Button>
              <Button variant="solid" colorScheme="red" onClick={cancelUploads} ml={3}>
                Yes, cancel uploads
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default Upload;
